import { useState, type InputHTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * A clock as it is typed, its digits put in place: `1120` is `11:20`, `9` is `09:`, `25` is
 * `02:5`, `11:7` is `11:07`; its colon written after the hour, not again while deleting it.
 */
export function clockTyped(next: string, was: string): string {
  let digits = next.replace(/\D/g, '');
  // A first digit no hour begins with: 7 is 07; an hour past 23, its first digit the hour.
  if (Number(digits[0]) > 2 || Number(digits.slice(0, 2)) > 23) digits = `0${digits}`;
  // A first digit no minute begins with: 7 is 07.
  if (Number(digits[2]) > 5) digits = `${digits.slice(0, 2)}0${digits.slice(2)}`;
  digits = digits.slice(0, 4);
  if (digits.length > 2) return `${digits.slice(0, 2)}:${digits.slice(2)}`;
  return digits.length === 2 && next.length >= was.length ? `${digits}:` : digits;
}

/**
 * The clock a text says, the last one when it says several, as a ticket writes it: `kell 20.30`,
 * `20:30`, `8:30 PM`, `8 pm`, `20h30`, `20時30分`, `Helsingi L 16.05.2026 kell 20.30` — as
 * `20:30`; none when it says none. A date's `16.05.2026` is never one.
 */
export function clockIn(text: string): string | null {
  const found = [
    ...text.matchAll(
      /(?<![\d.:])(\d{1,2})(?:\s*[:.hH時시]\s*(\d{2})(?![.\d])[分분]?)?(?:\s*([aApP])\.?\s*[mM]\.?(?!\p{L}))?/gu,
    ),
  ]
    .filter(([, , minutes, half]) => minutes !== undefined || half !== undefined)
    .map(([, hours = '', minutes = '00', half]) => {
      const hour = Number(hours);
      const pm = half?.toLowerCase() === 'p';
      const at =
        half === undefined ? hour : hour === 12 ? (pm ? 12 : 0) : pm ? hour + 12 : hour;
      return half !== undefined && hour > 12 ? null : [at, Number(minutes)];
    })
    .filter((clock): clock is number[] => clock !== null)
    .filter(([hours = 0, minutes = 0]) => hours < 24 && minutes < 60)
    .at(-1);
  return found ? found.map((part) => String(part).padStart(2, '0')).join(':') : null;
}

/** The clock a text put in says: the clock in its words, its digits alone as typed; else what
 * was there, as `kell 25.30` says none. */
function said(text: string, was: string): string {
  return clockIn(text) ?? (/^[\d\s:.]+$/.test(text) ? clockTyped(text, '') : was);
}

export interface ClockInputProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'value' | 'onChange' | 'type'
> {
  value: string;
  onChange: (value: string) => void;
  /** What it says while empty and not being typed in: `after 10:30`; else its mask. */
  placeholder?: string;
}

/**
 * A clock's field, the same everywhere: its mask, `hh:mm`, grey in place, each digit typed
 * taking its place — `1` reads `1h:mm`; a clock pasted read from whatever text says it.
 */
export function ClockInput({
  value,
  onChange,
  placeholder,
  className,
  onFocus,
  onBlur,
  ...rest
}: ClockInputProps) {
  const { t } = useTranslation();
  const mask = t('ticket.clockHint');
  const [focused, setFocused] = useState(false);
  const masked = (focused || value !== '') && value.length < mask.length;
  return (
    <span className="clock-input">
      <input
        {...rest}
        className={className ?? 'text-input'}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        placeholder={focused ? '' : (placeholder ?? mask)}
        value={value}
        onFocus={(event) => {
          setFocused(true);
          onFocus?.(event);
        }}
        onBlur={(event) => {
          setFocused(false);
          onBlur?.(event);
        }}
        onChange={(event) => {
          const next = event.target.value;
          // More than a key's worth at once — pasted, filled in, taken from a PDF: the clock it
          // says.
          onChange(
            next.length - value.length > 1 ? said(next, value) : clockTyped(next, value),
          );
        }}
        onPaste={(event) => {
          // A paste in place of what is there.
          const text = event.clipboardData.getData('text');
          event.preventDefault();
          onChange(said(text, value));
        }}
      />
      {masked && (
        <span className="text-input clock-mask" aria-hidden="true">
          <span className="clock-typed">{value}</span>
          {mask.slice(value.length)}
        </span>
      )}
    </span>
  );
}
